import { Button, Input, Modal, ModalFooter, ModalHeader, useAccessToken } from "modules/common";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { resetMobilePassword } from "services";

interface Props {
    isVisible: boolean;
    toggleModal: () => void;
}

const currentPasswordId = "reset-mobile-current-password";
const newPasswordId = "reset-mobile-new-password";
const repeatPasswordId = "reset-mobile-repeat-password";

export const ResetMobilePasswordModal: React.FC<Props> = ({
    isVisible,
    toggleModal,
}) => {
    const accessToken = useAccessToken();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [repeatPassword, setRepeatPassword] = useState("");
    const [errors, setErrors] = useState<{ current?: string; new?: string; repeat?: string }>({});
    const [hasValidated, setHasValidated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validate = (): boolean => {
        const nextErrors: { current?: string; new?: string; repeat?: string } = {};
        if (!currentPassword.trim()) {
            nextErrors.current = "Unesite trenutnu lozinku.";
        }
        if (!newPassword.trim()) {
            nextErrors.new = "Unesite novu lozinku.";
        } else if (newPassword.length < 6) {
            nextErrors.new = "Nova lozinka mora imati najmanje 6 znakova.";
        }
        if (newPassword !== repeatPassword) {
            nextErrors.repeat = "Lozinke se ne podudaraju.";
        } else if (!repeatPassword.trim()) {
            nextErrors.repeat = "Ponovite novu lozinku.";
        }
        setHasValidated(true);
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleClose = () => {
        setCurrentPassword("");
        setNewPassword("");
        setRepeatPassword("");
        setErrors({});
        setHasValidated(false);
        setIsSubmitting(false);
        toggleModal();
    };

    async function submit() {
        if (!validate() || isSubmitting || !accessToken) return;
        setIsSubmitting(true);
        try {
            await resetMobilePassword(currentPassword, newPassword, accessToken);
            toast.success("Lozinka mobilne aplikacije je uspješno promijenjena!");
            handleClose();
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.statuses?.[0]?.message ||
                "Greška pri promjeni lozinke.";
            toast.error(message);
            setIsSubmitting(false);
        }
    }

    return (
        <Modal
            isActive={isVisible}
            onClose={handleClose}
            modalHeader={
                <ModalHeader
                    onClose={handleClose}
                    title="Promjena lozinke mobilne aplikacije"
                    isLoading={isSubmitting}
                />
            }
            modalFooter={
                <ModalFooter>
                    <Button
                        type="button"
                        size="small"
                        variant="primary-ghost"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <span>Odustani</span>
                    </Button>
                    <Button
                        type="button"
                        size="small"
                        variant="warning"
                        onClick={submit}
                        disabled={isSubmitting || !accessToken}
                    >
                        <span>Promjeni lozinku</span>
                    </Button>
                </ModalFooter>
            }
        >
            <div className="reset-mobile-password-content mt-40 mr-40 mb-40 ml-40">
                <div className="mb-20">
                    <div className="reset-mobile-password-label-row">
                        <label htmlFor={currentPasswordId}>Lozinka</label>
                        {hasValidated && errors.current && (
                            <span className="error-message">{errors.current}</span>
                        )}
                    </div>
                    <Input
                        id={currentPasswordId}
                        name="currentPassword"
                        type="password"
                        size="medium"
                        placeholder="Unesite trenutnu lozinku"
                        value={currentPassword}
                        onChange={e => {
                            setCurrentPassword(e.target.value);
                            if (errors.current) setErrors(prev => ({ ...prev, current: undefined }));
                        }}
                        isValid={!errors.current}
                    />
                </div>
                <div className="mt-24 mb-20">
                    <div className="reset-mobile-password-label-row">
                        <label htmlFor={newPasswordId}>Nova lozinka</label>
                        {hasValidated && errors.new && (
                            <span className="error-message">{errors.new}</span>
                        )}
                    </div>
                    <Input
                        id={newPasswordId}
                        name="newPassword"
                        type="password"
                        size="medium"
                        placeholder="Unesite novu lozinku"
                        value={newPassword}
                        onChange={e => {
                            setNewPassword(e.target.value);
                            if (errors.new) setErrors(prev => ({ ...prev, new: undefined }));
                        }}
                        isValid={!errors.new}
                    />
                </div>
                <div className="mt-24">
                    <div className="reset-mobile-password-label-row">
                        <label htmlFor={repeatPasswordId}>Ponovite lozinku</label>
                        {hasValidated && errors.repeat && (
                            <span className="error-message">{errors.repeat}</span>
                        )}
                    </div>
                    <Input
                        id={repeatPasswordId}
                        name="repeatPassword"
                        type="password"
                        size="medium"
                        placeholder="Ponovite novu lozinku"
                        value={repeatPassword}
                        onChange={e => {
                            setRepeatPassword(e.target.value);
                            if (errors.repeat) setErrors(prev => ({ ...prev, repeat: undefined }));
                        }}
                        isValid={!errors.repeat}
                    />
                </div>
            </div>
        </Modal>
    );
};
